import { useState, useEffect, useRef } from 'react';
import {
  CheckCircleIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid';
import request from '../../../../lib/request';
import Bar from '../../../../components/progress/bar';
import PayDialog from '../../../../components/premium/payDialog';

const calculatePercentage = (current, total) => {
  return (current / total) * 100;
};

const Habit = ({ user, refreshUser, selectedDate, onClick }) => {
  const payDialogRef = useRef(null);

  const [habits, setHabits] = useState([]);
  const [habitCompletedCount, setHabitCompletedCount] = useState(0);

  useEffect(() => {
    getDailyHabits();
  }, [selectedDate]);

  const getDailyHabits = async () => {
    try {
      const { result } = await request.get(`/user/habit?date=${selectedDate}`);
      setHabits(result);
      setHabitCompletedCount(
        result.reduce((acc, cur) => (cur.completed ? acc + 1 : acc), 0)
      );
    } catch (err) {
      console.log(err);
    }
  };

  const handleCountClick = async (id, direction) => {
    try {
      let target = { ...habits.find((habit) => habit.id === id) };
      target.current += direction === 'inc' ? 1 : -1;
      target.current = Math.max(0, Math.min(target.count, target.current));

      const { result } = await request.put('/user/habit/log', {
        logDate: selectedDate,
        count: target.current,
        completed: target.count === target.current,
        habitId: id,
      });

      if (result) {
        result.current = result.count;
        delete result.count;

        let newHabits = [];

        if (target.current === target.count) {
          newHabits = habits.filter((habit) => habit.id !== id);
          newHabits.push({ ...target, ...result });
        }
        if (target.current < target.count) {
          if (target.completed) {
            newHabits = habits.filter((habit) => habit.id !== id);
            newHabits.unshift({ ...target, ...result });
          } else {
            newHabits = habits.map((habit) =>
              habit.id === id ? { ...target, ...result } : habit
            );
          }
        }

        setHabits(newHabits);
        setHabitCompletedCount(
          newHabits.reduce((acc, cur) => (cur.completed ? acc + 1 : acc), 0)
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleCheckClick = async (id) => {
    try {
      const target = habits.find((habit) => habit.id === id);
      const completed = !target.completed;
      const { result } = await request.put('/user/habit/log', {
        logDate: selectedDate,
        completed,
        habitId: id,
      });

      if (result) {
        let newHabits = [];

        if (!completed && target.completed) {
          newHabits = habits.filter((habit) => habit.id !== id);
          newHabits.unshift({
            ...target,
            ...result,
          });
        }
        if (completed && !target.completed) {
          newHabits = habits.filter((habit) => habit.id !== id);
          newHabits.push({
            ...target,
            ...result,
          });
        }
        setHabits(newHabits);
        setHabitCompletedCount(
          newHabits.reduce((acc, cur) => (cur.completed ? acc + 1 : acc), 0)
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openPayDialog = () => {
    payDialogRef.current.showModal();
  };

  const closePayDialog = () => {
    payDialogRef.current.close();
  };

  return user.activated || user.programRegistered ? (
    <div className='rounded-xl bg-secondary px-3 py-2 select-none'>
      <div className='flex items-center justify-between'>
        <div className='flex items-end gap-2'>
          <h1 className='text-white font-bold'>Daily Habit</h1>
          {habits.length > 0 ? (
            habitCompletedCount === habits.length ? (
              <div className='text-gray-400 text-xs'>completed</div>
            ) : (
              <div className='text-gray-400 text-xs'>
                {habitCompletedCount} / {habits.length}
              </div>
            )
          ) : null}
        </div>
        <div className='flex items-center gap-2'>
          <div
            className='text-xs text-white/50 underline'
            onClick={onClick}
          >
            Settings
          </div>
        </div>
      </div>
      {habits.length > 0 && (
        <div className='relative mt-1'>
          <div
            className={`relative space-y-3 max-h-32 overflow-y-auto scrollbar-hidden rounded-xl ${
              habits.length > 3 ? 'pt-4 pb-8' : ''
            }`}
          >
            {habits.map((habit, i) =>
              habit.type === 'COUNT' ? (
                <div
                  key={habit.id}
                  className='w-full flex items-center gap-2'
                >
                  <div className='flex items-center justify-center text-accent'>
                    {habit.completed ? (
                      <CheckCircleIcon className='size-6' />
                    ) : (
                      <div className='p-[3.2px]'>
                        <div className='size-[17.6px] rounded-full bg-tertiary' />
                      </div>
                    )}
                  </div>
                  <div className='w-full'>
                    <Bar
                      title={
                        <span
                          className={`${
                            habit.completed ? 'line-through text-white/40' : ''
                          }`}
                        >
                          {habit.name}
                        </span>
                      }
                      percentage={calculatePercentage(
                        Number(habit.current),
                        Number(habit.count)
                      )}
                      color='#eab308'
                      current={Number(habit.current)}
                      total={Number(habit.count)}
                      duration={300}
                    />
                  </div>
                  <div className='flex items-center gap-4 ml-4'>
                    <MinusCircleIcon
                      className='text-tertiary size-6 cursor-pointer'
                      onClick={() => handleCountClick(habit.id, 'dec')}
                    />
                    <PlusCircleIcon
                      className='text-tertiary size-6 cursor-pointer'
                      onClick={() => handleCountClick(habit.id, 'inc')}
                    />
                  </div>
                </div>
              ) : (
                <div
                  key={habit.id}
                  className='w-full flex items-center gap-2'
                >
                  <div
                    className='flex items-center justify-center text-accent'
                    onClick={() => handleCheckClick(habit.id)}
                  >
                    {habit.completed ? (
                      <CheckCircleIcon className='size-6' />
                    ) : (
                      <div className='p-[3.2px]'>
                        <div className='size-[17.6px] rounded-full bg-tertiary' />
                      </div>
                    )}
                  </div>
                  <div className='w-full flex items-center'>
                    <span
                      className={`text-xs ${
                        habit.completed
                          ? 'line-through text-white/40'
                          : 'text-white'
                      }`}
                    >
                      {habit.name}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>
          {habits.length > 3 && (
            <>
              <div className='absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-secondary to-transparent' />
              <div className='absolute -bottom-[1px] left-0 right-0 h-4 bg-gradient-to-t from-secondary to-transparent' />
            </>
          )}
        </div>
      )}
    </div>
  ) : (
    <div className='rounded-xl bg-secondary select-none'>
      <div className='flex items-center justify-between'>
        <div className='flex items-end gap-2'>
          <h1 className='text-white font-bold px-4 pt-4'>Daily Habit</h1>
        </div>
      </div>

      <div className='relative mt-2 overflow-hidden'>
        <div
          className={`relative space-y-4 max-h-32 overflow-y-auto scrollbar-hidden rounded-xl blur-sm px-4 pb-4`}
        >
          <div className='w-full flex items-center gap-2'>
            <div className='flex items-center justify-center text-accent'>
              <div className='p-[3.2px]'>
                <div className='size-[15.6px] rounded-full bg-tertiary' />
              </div>
            </div>
            <div className='w-full'>
              <Bar
                title={
                  <span className='text-xs text-white'>
                    Fresh walk after meal
                  </span>
                }
                percentage={calculatePercentage(1, 3)}
                color='#eab308'
                current={1}
                total={3}
                duration={300}
              />
            </div>
            <div className='flex items-center gap-4 ml-4'>
              <MinusCircleIcon className='text-tertiary size-6 cursor-pointer' />
              <PlusCircleIcon className='text-tertiary size-6 cursor-pointer' />
            </div>
          </div>
          <div className='w-full flex items-center gap-2'>
            <div className='flex items-center justify-center text-accent'>
              <div className='p-[3.2px]'>
                <div className='size-[15.6px] rounded-full bg-tertiary' />
              </div>
            </div>
            <div className='w-full flex items-center'>
              <span className={`text-xs text-white`}>Workout 30 minutes</span>
            </div>
          </div>
          <div className='w-full flex items-center gap-2'>
            <div className='flex items-center justify-center text-accent'>
              <CheckCircleIcon className='size-5' />
            </div>
            <div className='w-full'>
              <Bar
                title={
                  <span className='line-through text-white/40'>
                    Drink 8 cups of water
                  </span>
                }
                percentage={calculatePercentage(8, 8)}
                color='#eab308'
                current={8}
                total={8}
                duration={300}
              />
            </div>
            <div className='flex items-center gap-4 ml-4'>
              <MinusCircleIcon className='text-tertiary size-6 cursor-pointer' />
              <PlusCircleIcon className='text-tertiary size-6 cursor-pointer' />
            </div>
          </div>
        </div>

        <div className='absolute -top-1 left-0 w-full h-8 bg-gradient-to-b from-secondary to-transparent' />
        <div className='absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-secondary to-transparent rounded-b-xl' />
        <div className='absolute top-2 left-0 flex flex-col items-center justify-center w-full'>
          <div className='flex flex-col items-center justify-center gap-2'>
            <p className='text-center italic text-transparent bg-clip-text bg-gradient-to-tr from-white to-accent'>
              Small wins, big results
            </p>
            <button
              className='flex items-center justify-center gap-2 text-black text-center font-bold bg-accent px-4 py-2 rounded-full'
              onClick={openPayDialog}
            >
              <SparklesIcon className='size-6' />
              <span>Go Premium</span>
            </button>
          </div>
        </div>
      </div>
      <PayDialog
        dialogRef={payDialogRef}
        closeDialog={closePayDialog}
        title='Go Premium'
        refreshUser={refreshUser}
      />
    </div>
  );
};

export default Habit;
